import { TUNING } from '~/config/tuning'
import { ELEMENTS, type ElementKey, type RunState } from '~/types/game'

/**
 * Relics are persistent passive effects, hooked into the systems they
 * modify (effectiveStats, mission chance, encounter engine) — never
 * baked into stored state. Rarity is unassigned until simulations.
 */
export interface RelicDef {
  id: string
  name: string
  icon: string
  /** 🌟 Painted icon (v1.43): shown instead of the emoji where set —
   * the element charms wear the glowing element art. */
  image?: string
  description: string
  tags: string[]
  /** Charm element: +1 to all creatures, live. */
  element?: ElementKey
  /** Starting bonus id this relic duplicates — never offer both. */
  excludesBonus?: string
  /** Only meaningful while the injury system is enabled. */
  needsInjury?: boolean
  /** Only meaningful while mission expiry timers exist (v0.54 exp.). */
  needsExpiry?: boolean
  /** Only meaningful while creature speed varies (uniformSpeed off). */
  needsSpeed?: boolean
  /** 🏦 Banked (v1.76): out of every pool/shop, def kept for old saves. */
  banked?: boolean
  /** 🎗️ v6.54: a keepsake - never rolled, never sold, never listed
   * until met; reaches a run only as the pinned fourth card of its
   * first relic offer (data/keepsakes). */
  homage?: boolean
  simulation: { rarity: null; estimatedPower: null; notes: string }
}

const sim = { rarity: null, estimatedPower: null, notes: 'Rarity to be assigned after simulation.' }

function charm(id: string, name: string, icon: string, element: ElementKey, label: string): RelicDef {
  return {
    id,
    name,
    icon,
    element,
    description: `All creatures gain +1 ${label}.`,
    tags: ['relic', 'stat', element, 'global'],
    simulation: sim,
  }
}

export const RELIC_POOL: RelicDef[] = [
  // ---- 🎗️ The keepsake (v6.54) - does nothing; see data/keepsakes ----
  {
    id: 'relic-keepsake',
    name: 'The Keepsake',
    icon: '🎗️',
    description: 'A trinket a friend left in your pack. It does nothing at all.',
    tags: ['relic', 'keepsake'],
    homage: true,
    simulation: { rarity: null, estimatedPower: null, notes: 'no effect by design (homage chains)' },
  },
  // ---- Stat relics ----
  charm('relic-flame-charm', 'Flame Charm', '🔥', 'fire', 'Fire'),
  charm('relic-tide-charm', 'Tide Charm', '💧', 'water', 'Water'),
  charm('relic-root-charm', 'Root Charm', '🌿', 'grass', 'Grass'),
  charm('relic-storm-charm', 'Storm Charm', '⚡', 'lightning', 'Lightning'),
  charm('relic-stone-charm', 'Stone Charm', '🪨', 'earth', 'Earth'),
  {
    // Renamed from "Elemental Spark" — the old name suggested Lightning.
    // The id stays stable so saves and rarity data keep working.
    id: 'relic-elemental-spark',
    name: 'Apex Crest',
    icon: '✨',
    description: 'All creatures gain +1 to their highest element.',
    tags: ['relic', 'stat', 'peak', 'global'],
    excludesBonus: 'elemental-spark',
    simulation: sim,
  },
  {
    id: 'relic-balanced-training',
    name: 'Balanced Training',
    icon: '⚖️',
    description: 'All creatures gain +1 to their lowest element above 0.',
    tags: ['relic', 'stat', 'balance', 'global'],
    excludesBonus: 'balanced-training',
    simulation: sim,
  },
  {
    // v0.93 (per Javi): surges hit twice as hard. Rarity rides the
    // simulation ranking like most relics (v0.94 — the Epic pin came
    // off; the bots read it Rare: strong act 1, fades late).
    id: 'relic-overcharge',
    name: 'Overcharge Cell',
    icon: '🔋',
    description: 'Surge skills are DOUBLED: +4 to the element (or +2 to each for Dual Surge). Creatures with no Surge of their own gain a normal one (not doubled).',
    tags: ['relic', 'surge', 'global'],
    simulation: sim,
  },
  {
    id: 'relic-duo-core',
    name: 'Duo Core',
    icon: '☯️',
    description: 'Creatures with exactly two elements gain +1 to both.',
    tags: ['relic', 'stat', 'hybrid', 'duo', 'global'],
    simulation: sim,
  },
  {
    id: 'relic-specialist-badge',
    name: 'Specialist Badge',
    icon: '🎖️',
    description: 'Missions assigned to exactly one creature gain +15% success chance.',
    tags: ['relic', 'success_chance', 'solo', 'specialist'],
    simulation: sim,
  },
  {
    id: 'relic-purpose-charm',
    name: 'Purpose Charm',
    icon: '🎗️',
    description: 'All missions gain +1 creature slot.',
    tags: ['relic', 'slots', 'team', 'global'],
    simulation: sim,
  },

  // ---- XP relics ----
  {
    id: 'relic-training-manual',
    name: 'Training Manual',
    icon: '📖',
    description: 'Successful missions grant +1 XP.',
    tags: ['relic', 'xp', 'success', 'global'],
    excludesBonus: 'training-manual',
    simulation: sim,
  },
  {
    id: 'relic-team-journal',
    name: 'Team Journal',
    icon: '📓',
    description: 'After an assigned mission fails, the next mission you assign gains +10% success chance — and if it SUCCEEDS, its whole team is fully cured of injuries.',
    tags: ['relic', 'success_chance', 'failure', 'comeback', 'injury'],
    simulation: sim,
  },
  {
    id: 'relic-shared-lessons',
    name: 'Shared Lessons',
    icon: '🧑‍🏫',
    description: 'Failed assigned missions grant +3 extra XP.',
    tags: ['relic', 'xp', 'failure', 'global'],
    simulation: sim,
  },
  {
    id: 'relic-solo-practice',
    name: 'Solo Practice',
    icon: '🥋',
    description: 'A creature that completes a mission alone gains +2 bonus XP.',
    tags: ['relic', 'xp', 'solo', 'specialist'],
    simulation: sim,
  },
  {
    id: 'relic-study-group',
    name: 'Study Group',
    icon: '👥',
    description: 'When 2+ creatures complete a mission together, each gains +3 bonus XP (+5 each with 3+).',
    tags: ['relic', 'xp', 'group', 'team'],
    simulation: sim,
  },

  // ---- Safety relics ----
  {
    id: 'relic-safety-net',
    name: 'Safety Net',
    icon: '🕸️',
    description: 'The first failed or expired mission each encounter deals 0 damage.',
    tags: ['relic', 'safety', 'damage_prevention', 'encounter'],
    excludesBonus: 'safety-net',
    simulation: sim,
  },
  {
    id: 'relic-medic-band',
    name: 'Medic Band',
    icon: '🩹',
    description: 'Prevent the first injury each encounter.',
    tags: ['relic', 'injury', 'prevention', 'encounter'],
    excludesBonus: 'medic-band',
    needsInjury: true,
    simulation: sim,
  },
  {
    id: 'relic-safety-margin',
    name: 'Safety Margin',
    icon: '🛡️',
    description: 'Failed assigned missions AND expiring missions deal 1 less damage.',
    tags: ['relic', 'damage_reduction', 'failure', 'global'],
    simulation: sim,
  },
  {
    id: 'relic-close-enough',
    name: 'Close Enough',
    icon: '🤏',
    description: 'Missions that fail at 85%+ success chance cause no injuries.',
    tags: ['relic', 'injury', 'coverage', 'safety'],
    needsInjury: true,
    simulation: sim,
  },

  // ---- Coverage relics ----
  {
    id: 'relic-lucky-compass',
    name: 'Lucky Compass',
    icon: '🧭',
    description: 'Missions at 65%+ success chance gain +10% success chance.',
    tags: ['relic', 'success_chance', 'coverage', 'global'],
    simulation: sim,
  },
  {
    id: 'relic-desperation-coin',
    name: 'Desperation Coin',
    icon: '🪙',
    banked: true,
    description: 'Desperate missions fight harder: any chance below 50% is raised toward 50% (up to +25%).',
    tags: ['relic', 'success_chance', 'risky', 'global'],
    simulation: sim,
  },
  {
    id: 'relic-clean-coverage',
    name: 'Clean Coverage',
    icon: '✅',
    description: 'If every required element is at least partly covered, +10% success chance.',
    tags: ['relic', 'success_chance', 'coverage', 'hybrid'],
    simulation: sim,
  },
  // 🪙 WINDFALL (v2.71, per Javi): the spinner's third wedge. Gated
  // behind a relic on purpose — gamedesign.md's Important Rule is that
  // overkill/bonus coverage pays NOTHING by default, and is unlocked
  // through relics, traits or skills. This is the first one; more
  // payloads (XP, permanent stats, stardust) slot in beside it via
  // WINDFALL_RELICS in utils/battle.ts.
  {
    id: 'relic-prospectors-cut',
    name: "Prospector's Cut",
    icon: '🪙',
    description: 'A tenth of your win chance becomes a WINDFALL — a golden slice on the spinner. Land it and the mission also pays +10 gold.',
    tags: ['relic', 'gold', 'economy', 'windfall'],
    simulation: sim,
  },
  {
    id: 'relic-midas-charm',
    name: 'Midas Charm',
    icon: '👑',
    description: 'Gold missions pay DOUBLE.',
    tags: ['relic', 'gold', 'economy'],
    simulation: sim,
  },
  {
    id: 'relic-haggler-charm',
    name: "Haggler's Charm",
    icon: '🤝',
    description: 'Shop prices are HALVED.',
    tags: ['relic', 'gold', 'shop', 'economy'],
    simulation: sim,
  },
  {
    id: 'relic-missing-piece',
    name: 'Missing Piece',
    icon: '🧩',
    description: 'Every mission gets 1 virtual point toward its weakest requirement — the missing piece, filled.',
    tags: ['relic', 'coverage', 'virtual_stat', 'safety'],
    simulation: sim,
  },
  {
    id: 'relic-perfect-plan',
    name: 'Perfect Plan',
    icon: '📐',
    description: 'Missions completed at 100% success chance grant +5 bonus XP.',
    tags: ['relic', 'xp', 'perfect', 'encounter'],
    simulation: sim,
  },
  {
    id: 'relic-risk-taker',
    name: 'Risk Taker',
    icon: '🎲',
    description: 'Missions dispatched below 60% success chance grant +2 bonus XP if successful.',
    tags: ['relic', 'xp', 'risky', 'success'],
    simulation: sim,
  },

  // ---- Travel relics ----
  {
    id: 'relic-swift-boots',
    name: 'Swift Boots',
    icon: '👢',
    description: 'All creatures gain +1 Speed.',
    tags: ['relic', 'speed', 'travel', 'global'],
    needsSpeed: true,
    simulation: sim,
  },

  // ---- Timer relics ----
  {
    id: 'relic-warning-bell',
    name: 'Warning Bell',
    icon: '🔔',
    description: 'Creatures travel +2 Speed when dispatched to a mission with under 10 seconds remaining.',
    tags: ['relic', 'speed', 'travel', 'urgency'],
    needsExpiry: true,
    simulation: sim,
  },
  {
    id: 'relic-quick-whistle',
    name: 'Quick Whistle',
    icon: '📯',
    description: 'Fight timers are reduced by 2 seconds.',
    tags: ['relic', 'timer', 'fight_timer', 'speed', 'global'],
    simulation: sim,
  },
  {
    id: 'relic-calm-hourglass',
    name: 'Calm Hourglass',
    icon: '⏳',
    description: 'The first 5 missions each encounter have +10 seconds before expiring.',
    tags: ['relic', 'timer', 'expire_timer', 'encounter', 'safety'],
    needsExpiry: true,
    simulation: sim,
  },
  {
    id: 'relic-fast-deployment',
    name: 'Fast Deployment',
    icon: '🚀',
    description: 'The first mission assigned each encounter has no travel time.',
    tags: ['relic', 'travel', 'speed', 'encounter'],
    simulation: sim,
  },
  {
    id: 'relic-steady-clock',
    name: 'Steady Clock',
    icon: '🕰️',
    description: 'Each successful mission gives all waiting missions +5 seconds before expiring. Bosses keep their own time.',
    tags: ['relic', 'timer', 'expire_timer', 'momentum', 'global'],
    needsExpiry: true,
    simulation: sim,
  },
  {
    id: 'relic-short-fuse',
    name: 'Short Fuse',
    icon: '🧨',
    description: 'Missions expire 2 seconds sooner, but successful missions grant +3 XP.',
    tags: ['relic', 'timer', 'expire_timer', 'xp', 'risk_reward'],
    simulation: sim,
  },
  {
    // Renamed from "Focus Bell" (v1.65, per Javi) — the id stays stable
    // so saves and rarity data keep working.
    id: 'relic-focus-bell',
    name: 'Opening Flight',
    icon: '🕊️',
    description: 'The first 3 missions assigned each encounter resolve 35% faster.',
    tags: ['relic', 'timer', 'fight_timer', 'encounter', 'speed'],
    simulation: sim,
  },
  // 🧬 Creature-expansion relics (v2.82) — the synergy glue from §7 of
  // the roster proposal. Each opens a build the pool could not support.
  {
    id: 'relic-twinned-charm',
    name: 'Twinned Charm',
    icon: '👯',
    description: 'Missions assigned to exactly TWO creatures gain +10% success chance.',
    tags: ['relic', 'success_chance', 'duo', 'specialist'],
    simulation: sim,
  },
  {
    id: 'relic-reliquary-key',
    name: "Reliquary Key",
    icon: '🗝️',
    description: 'Every relic you own grants +0.5% success chance to every mission (rounded up, max +20%).',
    tags: ['relic', 'success_chance', 'scaling', 'relic_count'],
    simulation: sim,
  },
  {
    id: 'relic-sundial',
    name: 'Sundial',
    icon: '🌞',
    description: 'Missions dispatched with under 10 seconds left pay DOUBLE XP and DOUBLE gold.',
    tags: ['relic', 'xp', 'gold', 'expiry', 'risk'],
    needsExpiry: true,
    simulation: sim,
  },
  {
    id: 'relic-paupers-crown',
    name: "Pauper's Crown",
    icon: '👑',
    description: 'Taking the crown costs EVERY coin you hold. While you hold NO gold, every mission gains +15% success chance.',
    tags: ['relic', 'success_chance', 'gold', 'anti_economy'],
    simulation: sim,
  },
]

/* 🎨 Painted relic icons (v1.64, per Javi): every relic wears its own
 * art from art/UI/Relics. Keyed by relic id → filename in
 * public/ui/relics. A relic missing here falls back to its emoji. */
const RELIC_IMAGES: Record<string, string> = {
  'relic-flame-charm': 'flamecharm',
  'relic-tide-charm': 'tidecharm',
  'relic-root-charm': 'rootcharm',
  'relic-storm-charm': 'stormcharm',
  'relic-stone-charm': 'stonecharm',
  'relic-elemental-spark': 'apexcrest',
  'relic-balanced-training': 'balancedtraining',
  'relic-overcharge': 'overchargecell',
  'relic-duo-core': 'duocore',
  'relic-specialist-badge': 'specialistbadge',
  'relic-purpose-charm': 'purposecharm',
  'relic-training-manual': 'trainingmanual',
  'relic-team-journal': 'teamjournal',
  'relic-shared-lessons': 'hardearnedwisdom',
  'relic-solo-practice': 'solopractice',
  'relic-study-group': 'studygroup',
  'relic-safety-net': 'safetynet',
  'relic-medic-band': 'medicband',
  'relic-safety-margin': 'safetymargin',
  'relic-close-enough': 'closeenough',
  'relic-lucky-compass': 'luckycompass',
  'relic-desperation-coin': 'desperationcoin',
  'relic-clean-coverage': 'cleancoverage',
  'relic-midas-charm': 'midascharm',
  'relic-haggler-charm': 'hagglerscharm',
  'relic-missing-piece': 'missingpiece',
  'relic-keepsake': 'keepsake',
  'relic-perfect-plan': 'perfectplan',
  'relic-risk-taker': 'risktaker',
  'relic-swift-boots': 'swiftboots',
  'relic-warning-bell': 'warningbell',
  'relic-quick-whistle': 'quickwhistle',
  'relic-calm-hourglass': 'calmhourglass',
  'relic-fast-deployment': 'fastdeployment',
  'relic-steady-clock': 'steadyclock',
  'relic-short-fuse': 'shortfuse',
  'relic-focus-bell': 'openingflight',
  'relic-twinned-charm': 'twinnedcharm',
  'relic-reliquary-key': 'reliquarykey',
  'relic-sundial': 'sundial',
  'relic-paupers-crown': 'pauperscrown',
  'relic-prospectors-cut': 'prospectorscut',
}

/** 🖌️ v4.81 repaints (per Javi's art drop) — in-place replacements
 * ALWAYS need a cache-busting rev. */
const RELIC_ART_V2 = new Set([
  'relic-twinned-charm', 'relic-reliquary-key', 'relic-sundial',
  'relic-paupers-crown', 'relic-prospectors-cut',
])

for (const def of RELIC_POOL) {
  const file = RELIC_IMAGES[def.id]
  if (file) def.image = `/ui/relics/${file}.webp${RELIC_ART_V2.has(def.id) ? '?v=2' : ''}`
}

/** False while the relic's system (injuries / expiry timers / variable
 * speed) is disabled — gated relics leave every pool, shop and list.
 * Injuries exist on Ascension 1+ (v0.60), so pass the run's (or the
 * menu's unlocked) ascension level; plain `.filter(relicAvailable)` is
 * unsafe now — the array index would land in `ascension`. */
export function relicAvailable(def: RelicDef, ascension = 0): boolean {
  if (def.banked) return false
  if (def.homage) return false // 🎗️ pinned into a run's first offer instead (v6.54)
  if (def.needsInjury && !TUNING.injury.enabled && ascension < 1) return false
  if (def.needsExpiry && !TUNING.encounters.expiryEnabled) return false
  if (def.needsSpeed && TUNING.travel.uniformSpeed > 0) return false
  return true
}

export function getRelicDef(id: string): RelicDef {
  const found = RELIC_POOL.find(r => r.id === id)
  if (!found) throw new Error(`Unknown relic: ${id}`)
  return found
}

export function hasRelic(run: RunState, id: string): boolean {
  return run.relics.includes(id)
}

/** True when the run has this passive from either a bonus or a relic. */
export function hasPassive(run: RunState, bonusId: string): boolean {
  return run.bonusId === bonusId || run.relics.some(id => getRelicDef(id).excludesBonus === bonusId)
}

/** Charm elements owned, applied live in effectiveStats. */
export function ownedCharmElements(run: RunState): ElementKey[] {
  return run.relics
    .map(id => getRelicDef(id).element)
    .filter((e): e is ElementKey => !!e && ELEMENTS.includes(e))
}
